import {
  InterviewSession,
  InterviewSessionActivityLog,
} from "../../../Interface/Index";
import { useAIAssessmentContext } from "../../../Context/AIAssessmentContext/AIAssessmentContext";
import useAiAssessmentEvaluate from "./useAiAssessmentEvaluate";
import { useLoader } from "../../../Context/LoaderContext/LoaderContext";
import aiInterviewService from "../../../Services/ai-interview.service";

interface UseAiAssessmentViolationCaptureReturnType {
  captureFullScreenExit: () => Promise<any>;
  captureNotAllowedKeyPressing: (keys: string[]) => Promise<any>;
  captureMultipleTabOpening: () => Promise<any>;
  captureCursorFocusChange: () => Promise<any>;
  capturePageRefesh: (session?: InterviewSession) => Promise<any>;
  captureScreenRecordingStopped: () => Promise<any>;
  captureFullScreenExitAttempt: () => Promise<any>;
}

type ActivityLogResponseType = {
  isDisqualified: boolean;
  interviewSession: InterviewSession;
  jobApplication?: any;
} | null;

const useAiAssessmentViolationCapture =
  (): UseAiAssessmentViolationCaptureReturnType => {
    const { currentSession } = useAIAssessmentContext();

    const { handleUnqualifiedRound, stopAndUploadRecording } =
      useAiAssessmentEvaluate();

    const { setLoading, setLoadingMessage } = useLoader();

    const logViolationActivity = (
      session: InterviewSession,
      activity: InterviewSessionActivityLog
    ) => {
      if (!Array.isArray(session.activityLogs)) {
        session.activityLogs = [];
      }
      session.activityLogs.push(activity);
    };

    const handleViolationActivityLogResponse = async (
      response: ActivityLogResponseType
    ) => {
      setLoading(true);
      setLoadingMessage("Processing activity response...");
      try {
        if (response?.isDisqualified && response?.jobApplication) {
          await stopAndUploadRecording();
          await handleUnqualifiedRound(
            response.interviewSession,
          );
        }
        setLoading(false);
        setLoadingMessage(null);
        return response;
      } catch (error: any) {
        console.log("Error while handling activity log response", error);
        setLoading(false);
        setLoadingMessage(null);
        return null;
      }
    };

    const captureFullScreenExit = async () => {
      if (!currentSession) return;
      setLoading(true);
      setLoadingMessage("Processing activity response...");
      const activity: InterviewSessionActivityLog = {
        event: "fullscreen_exit",
        timestamp: new Date(),
      };
      logViolationActivity(currentSession, activity);
      const response = await aiInterviewService.addActivityLogs(
        currentSession._id,
        currentSession
      );
      return handleViolationActivityLogResponse(response);
    };

    const captureNotAllowedKeyPressing = async (keys: string[]) => {
      if (!currentSession) return;
      setLoading(true);
      setLoadingMessage("Processing activity response...");
      const activity: InterviewSessionActivityLog = {
        event: "keyboard_shortcut",
        key: keys.join("+"),
        timestamp: new Date(),
      };
      logViolationActivity(currentSession, activity);
      const response = await aiInterviewService.addActivityLogs(
        currentSession._id,
        currentSession
      );
      return handleViolationActivityLogResponse(response);
    };

    const captureMultipleTabOpening = async () => {
      if (!currentSession) return;
      setLoading(true);
      setLoadingMessage("Processing activity response...");
      const activity: InterviewSessionActivityLog = {
        event: "multiple_tabs",
        timestamp: new Date(),
      };
      logViolationActivity(currentSession, activity);
      const response = await aiInterviewService.addActivityLogs(
        currentSession._id,
        currentSession
      );
      return handleViolationActivityLogResponse(response);
    };

    const captureCursorFocusChange = async () => {
      if (!currentSession) return;
      setLoading(true);
      setLoadingMessage("Processing activity response...");
      const activity: InterviewSessionActivityLog = {
        event: "cursor_focus_switch",
        timestamp: new Date(),
      };
      logViolationActivity(currentSession, activity);
      const response = await aiInterviewService.addActivityLogs(
        currentSession._id,
        currentSession
      );
      return handleViolationActivityLogResponse(response);
    };

    const capturePageRefesh = async (session?: InterviewSession) => {
      const sessionToUse = session || currentSession;
      if (!sessionToUse) {
        console.log("capturePageRefesh: No current session found");
        return;
      }
      setLoading(true);
      setLoadingMessage("Processing activity response...");
      const activity: InterviewSessionActivityLog = {
        event: "refresh_page",
        timestamp: new Date(),
      };
      logViolationActivity(sessionToUse, activity);
      const response = await aiInterviewService.addActivityLogs(
        sessionToUse._id,
        sessionToUse
      );
      return handleViolationActivityLogResponse(response);
    };

    const captureScreenRecordingStopped = async () => {
      if (!currentSession) return;
      setLoading(true);
      setLoadingMessage("Processing activity response...");
      const activity: InterviewSessionActivityLog = {
        event: "screen_recording_stopped",
        timestamp: new Date(),
      };
      logViolationActivity(currentSession, activity);
      const response = await aiInterviewService.addActivityLogs(
        currentSession._id,
        currentSession
      );
      return handleViolationActivityLogResponse(response);
    };

    const captureFullScreenExitAttempt = async () => {
      if (!currentSession) return;
      setLoading(true);
      setLoadingMessage("Processing activity response...");
      const activity: InterviewSessionActivityLog = {
        event: "fullscreen_exit_attempt",
        timestamp: new Date(),
      };
      logViolationActivity(currentSession, activity);
      const response = await aiInterviewService.addActivityLogs(
        currentSession._id,
        currentSession
      );
      return handleViolationActivityLogResponse(response);
    };

    return {
      captureFullScreenExit,
      captureNotAllowedKeyPressing,
      captureMultipleTabOpening,
      captureCursorFocusChange,
      capturePageRefesh,
      captureScreenRecordingStopped,
      captureFullScreenExitAttempt,
    };
  };

export default useAiAssessmentViolationCapture;
